import { useQuery } from '@tanstack/react-query';
import {
  getForecastDate,
  getForecastDates,
  isValidForecastCoordinates,
  normalizeLocationForecast,
  type LocationForecast,
  type LocationForecastPayload,
} from '@/lib/forecast';
import { publicClient } from '@/services/amplifyClient';
import { computeLocationForecastClient } from '@/services/clientForecast';

export type { FactorExplanation, LocationForecast } from '@/lib/forecast';

interface UseLocationForecastOptions {
  latitude: number;
  longitude: number;
  date?: string; // YYYY-MM-DD format, defaults to today
  locationId?: string;
  timezone?: string;
  enabled?: boolean;
}

// GraphQL query for getting location forecast
const GET_LOCATION_FORECAST = /* GraphQL */ `
  query GetLocationForecast(
    $latitude: Float!
    $longitude: Float!
    $date: String!
    $locationId: String
    $timezone: String
  ) {
    getLocationForecast(
      latitude: $latitude
      longitude: $longitude
      date: $date
      locationId: $locationId
      timezone: $timezone
    ) {
      locationId
      latitude
      longitude
      date
      sunriseTime
      sunsetTime
      sunriseQuality
      sunsetQuality
      sunriseCategory
      sunsetCategory
      sunriseExplanation
      sunsetExplanation
      sunriseFactorBreakdown
      sunsetFactorBreakdown
      goldenHourMorningStart
      goldenHourMorningEnd
      goldenHourEveningStart
      goldenHourEveningEnd
      blueHourMorningStart
      blueHourMorningEnd
      blueHourEveningStart
      blueHourEveningEnd
      cloudCover
      cloudCoverLow
      cloudCoverMid
      cloudCoverHigh
      humidity
      visibility
      windSpeed
      temperature
      pressure
      factors
      cached
      fetchedAt
      expiresAt
    }
  }
`;

interface ForecastRequest {
  latitude: number;
  longitude: number;
  date: string;
  locationId?: string;
  timezone: string;
}

async function fetchLocationForecast({
  latitude,
  longitude,
  date,
  locationId,
  timezone,
}: ForecastRequest): Promise<LocationForecast> {
  try {
    const response = await publicClient.graphql({
      query: GET_LOCATION_FORECAST,
      variables: { latitude, longitude, date, locationId, timezone },
    });

    if ('errors' in response && response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    const data = 'data' in response ? response.data : null;
    if (data && 'getLocationForecast' in data && data.getLocationForecast) {
      return normalizeLocationForecast(
        data.getLocationForecast as unknown as LocationForecastPayload,
        timezone
      );
    }
  } catch {
    // AppSync unavailable (expired API key, etc.) — fall through to client compute.
  }

  return (await computeLocationForecastClient({
    latitude,
    longitude,
    date,
    locationId,
    timezone,
  })) as LocationForecast;
}

/**
 * Hook to fetch forecast predictions for a specific location
 * Uses the getLocationForecast GraphQL query backed by a Lambda function
 */
export function useLocationForecast({
  latitude,
  longitude,
  date,
  locationId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  enabled = true,
}: UseLocationForecastOptions) {
  const forecastDate = date || getForecastDate(timezone);

  const queryKey = [
    'locationForecast',
    latitude,
    longitude,
    forecastDate,
    locationId,
    timezone,
  ];

  const {
    data: forecast,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchLocationForecast({
        latitude,
        longitude,
        date: forecastDate,
        locationId,
        timezone,
      }),
    enabled: enabled && isValidForecastCoordinates(latitude, longitude),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 1,
  });

  return {
    forecast,
    isLoading,
    error,
    refetch,
  };
}

interface UseLocationForecastRangeOptions {
  latitude: number;
  longitude: number;
  days?: number; // Number of days to fetch, defaults to 7
  locationId?: string;
  timezone?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch forecast predictions for a location over multiple days (7-day forecast)
 * Fetches forecasts for each day in parallel for efficiency
 */
export function useLocationForecastRange({
  latitude,
  longitude,
  days = 7,
  locationId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  enabled = true,
}: UseLocationForecastRangeOptions) {
  const dates = getForecastDates(days, timezone);

  const queryKey = [
    'locationForecastRange',
    latitude,
    longitude,
    days,
    locationId,
    timezone,
    dates[0],
  ];

  const {
    data: forecasts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const promises = dates.map((date) =>
        fetchLocationForecast({ latitude, longitude, date, locationId, timezone }).catch(() => null)
      );

      const results = await Promise.all(promises);

      // Create a map of date -> forecast
      const forecastsByDate: Record<string, LocationForecast | null> = {};
      dates.forEach((date, index) => {
        forecastsByDate[date] = results[index] ?? null;
      });

      return {
        forecastsByDate,
        dates,
        forecasts: results.filter((f): f is LocationForecast => f !== null),
      };
    },
    enabled: enabled && isValidForecastCoordinates(latitude, longitude),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 2,
  });

  return {
    forecastsByDate: forecasts?.forecastsByDate ?? {},
    dates: forecasts?.dates ?? dates,
    allForecasts: forecasts?.forecasts ?? [],
    isLoading,
    error,
    refetch,
  };
}
